import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    provideAppInitializer,
    provideZoneChangeDetection,
    ErrorHandler, LOCALE_ID
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideFuse } from '@fuse/fuse.provider';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from './app.routes';
import { provideAuth } from '@core/auth/auth.provider';
import { provideIcons } from '@core/icons/icons.provider';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { AppSettingService } from './core/settings/app-setting.service';
import { apiUrlInterceptor } from './core/settings/api-url.interceptor';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { CustomMatPaginatorIntl } from './common/services/custom-mat-paginator-intl.service';

import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';
import { LanguageService } from './common/services/language.service';
import { BASE_PATH } from './common/generated';

registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEn, 'en');


// Custom error handler to better track initialization errors
class AppErrorHandler implements ErrorHandler {
    handleError(error: any): void {
        console.warn('Application Error:', error);
    }
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideAnimations(),
        provideHttpClient(withInterceptors([apiUrlInterceptor])),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),
        // Register custom error handler
        { provide: ErrorHandler, useClass: AppErrorHandler },

        // Material Date Adapter
        { provide: DateAdapter, useClass: LuxonDateAdapter },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: { dateInput: 'D', },
                display: { dateInput: 'DDD', monthYearLabel: 'LLL yyyy', dateA11yLabel: 'DD', monthYearA11yLabel: 'LLLL yyyy' },
            },
        },
        {
            provide: LOCALE_ID,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                // Retourne la langue active actuelle
                return translocoService.getActiveLang();
            },
            deps: [TranslocoService]
        },
        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    { id: 'fr', label: 'Français', },
                    { id: 'en', label: 'English', },
                ],
                defaultLang: 'fr',
                fallbackLang: 'fr',
                reRenderOnLangChange: true,
                prodMode: isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            try {
                const translocoService = inject(TranslocoService);
                const languageService = inject(LanguageService);
                const appSettingService = inject(AppSettingService);

                return Promise.all([appSettingService.loadConfig(), languageService.initializeLanguage()])
                    .then(([config, language]) => {
                        // 🔒 S'assurer que Transloco utilise la bonne langue et attendre le chargement
                        if (language !== translocoService.getActiveLang()) {
                            translocoService.setActiveLang(language);
                        }

                        // 🔑 NOUVEAU : Attendre que Transloco charge les traductions
                        return translocoService.load(language).toPromise().then(() => { return Promise.resolve(); });
                    }).catch(error => {
                        console.error('🚨 Error during app initialization:', error);
                        // Continuer même en cas d'erreur pour éviter de bloquer l'app
                        return Promise.resolve();
                    });
            } catch (error) {
                console.error('Error in app initializer:', error);
                return Promise.resolve();
            }
        }),
        {
            provide: BASE_PATH,
            useFactory: (appSettingService: AppSettingService) => appSettingService.getApiUrl(),
            deps: [AppSettingService]
        },
        provideAuth(),
        provideIcons(),
        provideFuse({
            fuse: {
                layout: 'compact',
                scheme: 'light',
                screens: { sm: '600px', md: '960px', lg: '1280px', xl: '1440px' },
                theme: 'theme-africaiq',
                themes: [
                    { id: 'theme-africaiq', name: 'AfricaIQ', },
                    { id: 'theme-default', name: 'Default', },
                    { id: 'theme-brand', name: 'Brand', },
                ],
            },
        }),
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: { appearance: 'outline' }
        },
        {
            provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
            useValue: { position: 'above' }
        },
        {
            provide: MatPaginatorIntl,
            useClass: CustomMatPaginatorIntl
        }
    ],
};
